<template>
  <div class="stock-match-container">
    <div v-if="hasFullMatch" class="badge">
      {{ i18n.t('/stockmatch/fullmatch') }}
    </div>
    <app-button data-qa="show-stock-modal-button" @click="toggleStockModal(true)">
      {{ i18n.t('/stockmatch/showstock') }}
    </app-button>

    <div class="counter-wrapper" style="cursor: pointer" @click="toggleStockModal(true, false)">
      <icon icon="car" large />
      <div class="counter" :class="{ green: resultsDealerLength > 0 }">
        {{ resultsDealerLength }}
      </div>
    </div>

    <div class="counter-wrapper" style="cursor: pointer" @click="toggleStockModal(true, true)">
      <icon icon="map" large />
      <div class="counter" :class="{ green: resultsCountryLength > 0 }">
        {{ resultsCountryLength }}
      </div>
    </div>

    <modal
      v-if="showStockModal"
      max-width="100%"
      :title="i18n.t('/stockmatch/modaltitle')"
      class="cars-list-modal"
      @close="toggleStockModal(false)"
    >
      <div class="cars-container">
        <div class="info-label">
          <h5>
            {{ i18n.t('/stockmatch/infolabel') }}
            <button @click="showInfoModal = true">
              <icon icon="info-plain" />
            </button>
          </h5>
        </div>
        <div class="match-filters">
          <dl>
            <dt>{{ i18n.t('/stockmatch/essentialmatchlabel') }}</dt>
            <dd class="drive">
              <form-toggle
                id="filter-drive"
                v-model="filterDrive"
                :title="i18n.t('/stockmatch/filterdrivelabel')"
                @input="fetchStock"
              />
            </dd>
            <dd>
              <form-toggle
                id="filter-color"
                v-model="filterColor"
                :title="i18n.t('/stockmatch/filtercolorlabel')"
                @input="fetchStock"
              />
            </dd>
            <dd>
              <form-toggle
                id="filter-interior"
                v-model="filterInterior"
                :title="i18n.t('/stockmatch/filterinteriorlabel')"
                @input="fetchStock"
              />
            </dd>
            <dt v-if="configOptions.length" class="spec-group options-heading">
              {{ i18n.t('/stockmatch/filteroptionslabel') }}
            </dt>
            <dd v-for="option in configOptions" :key="option.shortId" class="spec-group option">
              <form-toggle
                :id="`filter-option-${option.shortId}`"
                v-model="filterOptions[option.shortId]"
                :title="option.shortId"
                @input="fetchStock"
              />
            </dd>
          </dl>
          <div class="standard-options-container">
            <form-toggle
              id="show-standard-options"
              v-model="showStandardOptions"
              :title="i18n.t('/stockmatch/showstandardoptionslabel')"
            />
          </div>
        </div>

        <dl class="chosen-car">
          <dt class="title-wrapper">
            {{ `${configuration.ModelName} ${configuration.TrimlineName}` }}
          </dt>
          <dd class="image-wrapper">
            <img
              :src="configuration.Images.Exterior"
              :alt="`${configuration.ModelName} ${configuration.TrimlineName}`"
              class="car-image"
            />
          </dd>
          <dd class="config-type">
            <h4>{{ i18n.t('/stockmatch/yourconfiglabel') }}</h4>
          </dd>
          <dd class="full-match-container"></dd>
          <dd class="price">
            {{ i18n.n(configuration.Price.Incl, 'currency') }}
          </dd>
          <dd class="drive">{{ configuration.CatalogCarName }}</dd>
          <dd>{{ getColorDescription(configuration.Color) }}</dd>
          <dd>
            {{
              configuration.Interior
                ? getColorDescription(configuration.Interior.UpholsteryColor)
                : ''
            }}
          </dd>
          <dd v-if="configOptions.length" class="spec-group">&nbsp;</dd>
          <dd v-for="option in configOptions" :key="option.shortId" class="spec-group option">
            <span>{{ `${option.shortId} - ${option.name}` }}</span>
          </dd>
        </dl>

        <div class="matching-cars-viewport">
          <loader v-if="isLoading" type="alert" />
          <ul
            v-else-if="
              (showNationWideStock && allCars.length) || matchingStockCars.resultsDealer.length
            "
            class="matching-cars"
          >
            <li v-for="car in carList" :key="car.CommissionNumber" class="car-column">
              <legacy-matching-stock-car
                :image-url="car.ImageExterior ? car.ImageExterior : undefined"
                :display-name="`${configuration.ModelName} ${car.Trimline}`"
                :price="parseFloat(car.ConsumerPrice)"
                :engine="car.EngineDescription"
                :transmission="car.Transmission"
                :color-code="car.ColorCode + ' ' + car.Color"
                :interior-code="car.InteriorCode"
                :options="getMatchOptions(car)"
                :is-full-match="car.IsFullMatch"
                :matching-items="getMatchingItems(car)"
                :car-status="i18n.t(`/stockmatch/carstatus/${car.CarStatus}`)"
                :kom-number="
                  (matchingStockCars.resultsDealer.includes(car)
                    ? i18n.t('/stockmatch/komnumber/dealer')
                    : i18n.t('/stockmatch/komnumber/national')) +
                  ' ' +
                  car.CommissionNumber
                "
                @stockcar-details="setStockCarDetails(car.CommissionNumber)"
                @reservation-status="setReservationStatusId(car.CommissionNumber)"
                @select-car="showMatchingStockCarToNewQuotePrompt(car)"
              />
            </li>
          </ul>
          <p v-else class="no-stock-message">
            {{ i18n.t('/stockmatch/nomatchestext') }}
          </p>
        </div>

        <div class="container-actions">
          <form-toggle
            id="show-nation-wide-stock"
            v-model="showNationWideStock"
            :title="i18n.t('/stockmatch/shownationwidelabel')"
          />
        </div>

        <modal
          v-if="showInfoModal"
          max-width="70%"
          :title="i18n.t('/stockmatch/infomodaltitle')"
          @close="toggleInfoModal(false)"
        >
          <p v-html="i18n.t('/stockmatch/infomodalbody')"></p>
        </modal>
      </div>
    </modal>

    <stock-car-detail-new
      v-if="stockCarDetails && dealerSettings.newStockFlowEnabled"
      :stock-car="stockCarDetails"
      @on-error="setStockCarDetails(null)"
      @on-cancel="setStockCarDetails(null)"
      @close="setStockCarDetails(null)"
    />
    <stock-car-detail
      v-else-if="stockCarDetails && !dealerSettings.newStockFlowEnabled"
      :stock-car-id="stockCarDetails.Id"
      :title="`${stockCarDetails.Configuration.ModelName} ${stockCarDetails.Configuration.TrimlineName}`"
      @on-error="setStockCarDetails(null)"
      @on-cancel="setStockCarDetails(null)"
      @close="setStockCarDetails(null)"
    />

    <matching-stock-car-prompt
      :show="showMatchingStockCarPrompt"
      @close="closeMatchingStockCarToNewQuotePrompt()"
      @confirm="applyToNewQuote"
    />

    <modal
      v-if="stockCarForReservationStatus && dealerSettings.newStockFlowEnabled"
      id="stock-car-reservation-status"
      :title="i18n.t('/StockDashboard/ReservationStatus/Title')"
      @close="setReservationStatusId(null)"
    >
      <stock-reservation-status-new :stock-car="stockCarForReservationStatus" />
    </modal>
    <modal
      v-else-if="stockCarForReservationStatus && !dealerSettings.newStockFlowEnabled"
      id="stock-car-reservation-status"
      :title="i18n.t('/StockDashboard/ReservationStatus/Title')"
      @close="setReservationStatusId(null)"
    >
      <stock-reservation-status :stock-car-id="stockCarForReservationStatus.Id" />
    </modal>
  </div>
</template>

<script>
import AppButton from '@/components/generic/presentational/atoms/appButton/AppButton.vue'
import Modal from '@/components/generic/presentational/molecules/modal/Modal.vue'
import Icon from '@/components/generic/presentational/atoms/icon/Icon.vue'
import FormToggle from '@/components/generic/presentational/atoms/formCheckbox/FormToggle.vue'
import LegacyMatchingStockCar from '@/components/generic/presentational/molecules/matchingStockCar/LegacyMatchingStockCar.vue'
import MatchingStockCarPrompt from '@/components/generic/presentational/molecules/matchingStockCarPrompt/MatchingStockCarPrompt.vue'
import { mapMutations, mapState, mapActions } from 'vuex'
import StockMatching from '@/api/server/StockMatching'
import Loader from '@/components/generic/presentational/atoms/loader/Loader.vue'
import StockCarDetailNew from '@/components/generic/presentational/organisms/stockDashboardNew/stockCarDetail/StockCarDetailNew.vue'
import StockReservationStatusNew from '@/components/generic/presentational/organisms/stockDashboardNew/StockReservationStatusNew.vue'
import StockReservationStatus from '@/components/generic/presentational/molecules/stockReservationStatus/StockReservationStatus.vue'
import StockCarDetail from '@/components/generic/presentational/organisms/stockCarDetail/StockCarDetail.vue'
import StockCarsNewApi from '@/api/server/StockCarsNew'
import stockCarsService from '@/services/stockCarsService'
import { union, sortBy, difference } from 'lodash'
import stockMatchOptionType from '@/helpers/enums/stockMatchOptionType'
import moment from 'moment'

export default {
  components: {
    AppButton,
    Modal,
    Icon,
    FormToggle,
    LegacyMatchingStockCar,
    Loader,
    StockCarDetailNew,
    StockCarDetail,
    StockReservationStatusNew,
    StockReservationStatus,
    MatchingStockCarPrompt,
  },
  data() {
    return {
      i18n: window.VUE_CLI_EXPORTS.plugins.i18n,
      showStockModal: false,
      showInfoModal: false,
      showNationWideStock: false,
      showStandardOptions: false,
      filterDrive: false,
      filterColor: false,
      filterInterior: false,
      filterOptions: {},
      isLoading: true,
      stockCarDetails: null,
      stockCarForReservationStatus: null,
      showMatchingStockCarPrompt: false,
      selectedMatchedStockCar: null,
    }
  },
  computed: {
    ...mapState('stockcars', ['matchingStockCars']),
    ...mapState('quote', ['quote']),
    ...mapState('configuration', ['configurationCode', 'configuration', 'price']),
    ...mapState('user', ['dealerSettings']),
    allCars() {
      return (
        this.matchingStockCars.resultsDealer?.concat(this.matchingStockCars.resultsCountry) || []
      )
    },
    hasFullMatch() {
      return !!this.allCars.find((match) => match.IsFullMatch)
    },
    configOptions() {
      const options = []
      this.configuration.Categories?.forEach((cat) => {
        cat.Features.forEach((opt) => {
          options.push({
            shortId: opt.ShortId,
            name: opt.Name,
          })
        })
      })
      return options
    },
    hasActiveFilters() {
      return (
        this.filterDrive ||
        this.filterColor ||
        this.filterInterior ||
        Object.keys(this.filterOptions).length !== 0
      )
    },
    carList() {
      return this.showNationWideStock ? this.allCars : this.matchingStockCars.resultsDealer
    },
    localOptionsUnion() {
      return this.sortMatchOptions(this.matchingStockCars.resultsDealer)
    },
    nationwideOptionsUnion() {
      return this.sortMatchOptions(this.allCars)
    },
    resultsCountryLength() {
      return this.matchingStockCars.resultsCountry.length
    },
    resultsDealerLength() {
      return this.matchingStockCars.resultsDealer.length
    },
  },
  watch: {
    configurationCode() {
      this.clearFilters()
      this.clearMatchedStockCars()
      this.fetchStock()
    },
  },
  mounted() {
    this.fetchStock()
  },
  unmounted() {
    this.clearFilters()
    this.clearMatchedStockCars()
  },
  methods: {
    ...mapMutations({
      setMatchingStockCars: 'stockcars/setMatchingStockCars',
      setShouldCreateMatchingStockQuote: 'quote/setShouldCreateMatchingStockQuote',
      setPrevQuote: 'quote/setPrevQuote',
      setCreateMatchingStockQuoteOptions: 'quote/setCreateMatchingStockQuoteOptions',
    }),
    ...mapActions({
      setLoading: 'quote/setLoading',
      setStockButtonDate: 'quote/setStockButtonDate',
    }),
    clearFilters() {
      this.filterDrive = false
      this.filterColor = false
      this.filterInterior = false
      this.filterOptions = {}
    },
    clearMatchedStockCars() {
      this.isLoading = true
      this.setMatchingStockCars({})
    },
    async fetchStock() {
      if (!this.configurationCode) return

      const options = []
      for (const [optId, checked] of Object.entries(this.filterOptions)) {
        if (checked) options.push(optId)
      }
      try {
        const matchingStock = await StockMatching.getMatchingStockCars(this.configurationCode, {
          engine: this.filterDrive,
          transmission: this.filterDrive,
          interior: this.filterInterior,
          color: this.filterColor,
          options,
        })
        this.setMatchingStockCars(matchingStock)
        this.isLoading = false
      } catch (e) {}
    },
    toggleStockModal(show, showNationWideStock = false) {
      this.showNationWideStock = showNationWideStock
      this.showStockModal = show
      if (show) {
        let dateNow = moment.utc().format(moment.HTML5_FMT.DATETIME_LOCAL_SECONDS)
        this.setStockButtonDate(dateNow)
      }

      if (show && this.isLoading) {
        this.fetchStock()
      }
    },
    toggleInfoModal(show) {
      this.showInfoModal = show
    },
    getMatchingItems(car) {
      const matchingItems = []
      if (car.MatchEngine === 1) matchingItems.push('engine')
      if (car.MatchTransmission === 1) matchingItems.push('transmission')
      if (car.MatchColor === 1) matchingItems.push('colorCode')
      if (car.MatchInterior === 1) matchingItems.push('interiorCode')
      if (car.ConsumerPrice === this.price.Incl) matchingItems.push('price')
      car.StockOptions?.split(' ').forEach((stockOptId) => {
        if (this.configOptions.find((cfgOpt) => cfgOpt.shortId === stockOptId))
          matchingItems.push(`option-${stockOptId}`)
      })
      return matchingItems
    },
    getMatchOptions(car) {
      const matchingConfigOptions = this.configOptions.map((option, index) =>
        car.StockOptions?.split(' ').includes(option.shortId)
          ? option
          : { shortId: `missing${index}`, name: ' ' }
      )
      const extraOptions = []
      const unionList = this.showNationWideStock
        ? this.nationwideOptionsUnion
        : this.localOptionsUnion

      // Use union list to display all options for each car beside each other
      unionList.forEach((option, index) => {
        const matchOption = car.MatchOptions.find((x) => x.ShortId === option)
        extraOptions.push(
          matchOption
            ? {
                shortId: matchOption.ShortId,
                name: matchOption.Name,
                type: matchOption.Type,
              }
            : { shortId: `missing-extra${index}`, name: ' ' }
        )
      })
      return matchingConfigOptions.concat(extraOptions)
    },
    showMatchingStockCarToNewQuotePrompt(car) {
      this.selectedMatchedStockCar = car
      this.showMatchingStockCarPrompt = true
    },
    closeMatchingStockCarToNewQuotePrompt() {
      this.selectedMatchedStockCar = null
      this.showMatchingStockCarPrompt = false
    },
    applyToNewQuote(options) {
      let car = this.selectedMatchedStockCar
      this.setPrevQuote(this.quote)
      this.setLoading(true)
      this.setShouldCreateMatchingStockQuote(true)
      this.setCreateMatchingStockQuoteOptions(options)
      this.clearMatchedStockCars()
      this.$router.push({
        name: 'new-stockcar-komnumber-page',
        params: {
          komnummer: car.CommissionNumber,
          cstType: this.$route.params.cstType,
        },
      })
    },
    getColorDescription(color) {
      return color ? color.ShortId + ' ' + color.Name : ''
    },
    sortMatchOptions(optionsList) {
      // Get only additional options first
      var additionalOptionsList = optionsList?.map((x) =>
        x.MatchOptions.filter(
          (y) =>
            y.ShortId?.length &&
            y.TypeOption === 'OptionsMore' &&
            (y.Type === stockMatchOptionType.additional ||
              y.Type === stockMatchOptionType.aditional)
        ).map((z) => z.ShortId)
      )
      if (!optionsList) {
        return optionsList
      }
      additionalOptionsList = sortBy(union(...additionalOptionsList))

      if (this.showStandardOptions) {
        // Sort standard options if to be shown
        var standardOptionsList = union(
          ...optionsList?.map((x) =>
            x.MatchOptions.filter((y) => y.ShortId?.length && y.TypeOption === 'OptionsMore').map(
              (z) => z.ShortId
            )
          )
        )
        standardOptionsList = sortBy(difference(standardOptionsList, additionalOptionsList))

        additionalOptionsList = additionalOptionsList.concat(standardOptionsList)
      }
      return additionalOptionsList
    },
    async fetchStockCarByCommissionNumber(commissionNumber) {
      if (this.dealerSettings.newStockFlowEnabled) {
        const results = await StockCarsNewApi.getStockcarsNew({
          search: commissionNumber,
          size: 1,
          page: 1,
          includeNationWide: true,
        })
        return results?.[0] ?? null
      }
      return await stockCarsService.getStockCarByKomnumber(commissionNumber)
    },
    async setStockCarDetails(commissionNumber) {
      this.stockCarDetails = commissionNumber
        ? await this.fetchStockCarByCommissionNumber(commissionNumber)
        : null
    },
    async setReservationStatusId(commissionNumber) {
      this.stockCarForReservationStatus = commissionNumber
        ? await this.fetchStockCarByCommissionNumber(commissionNumber)
        : null
    },
  },
}
</script>

<style lang="scss" scoped>
.stock-match-container {
  display: flex;
  gap: 0.9375rem;
  margin-top: 0.3125rem;

  button {
    margin: 0;
  }

  .counter-wrapper {
    position: relative;
    transition: transform 0.5s;

    &:hover {
      transform: scale(1.5);
    }

    .counter {
      position: absolute;
      top: -5px;
      right: 0;
      background-color: $color-danger;
      color: #ffffff;
      font-size: 9px;
      padding: 2px 6px;
      border-radius: 50%;
    }

    .green {
      background-color: $color-success;
    }

    .letter {
      text-align: center;
      font-size: 25px;
      line-height: 45px;
      font-weight: 700;
    }
  }
}

.badge {
  background: $color-success;
  display: inline-block;
  font-size: 0.75rem;
  padding: 0.1rem 0.25rem;
  border-radius: 0.25rem;
  color: $white;
}
button {
  margin-left: 0.25em;
}

.cars-list-modal {
  --column-width: 240px;
  font-size: $font-size-base;
  line-height: $line-height-base;

  :deep(dl) {
    display: flex;
    flex-direction: column;
    padding: 0.5rem 0 2rem;
  }
  :deep(dd),
  :deep(dt) {
    min-height: $line-height-base * 1em;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  :deep(dd) {
    order: 2;
    margin-bottom: 1rem;

    & span {
      transition: all 150ms;
    }

    &.option:hover {
      overflow: visible;
      z-index: 99;

      & span {
        background: $gray-lightest;
        position: relative;
        box-shadow: $shadow-md;
        padding: 0 0.25rem;
      }
    }
  }
  :deep(dt),
  :deep(.price) {
    font-weight: $font-weight-bold;
  }
  :deep(.image-wrapper) {
    order: 0;
    text-align: center;
    margin-bottom: 0px;
  }
  :deep(.config-type) {
    order: 0;
    margin-bottom: 0px;
    text-align: center;
  }
  :deep(.full-match-container) {
    order: 0;
    display: inline-block;
    font-size: 0.75rem;
    line-height: 1rem;
    font-weight: $font-weight-bold;
    color: $color-success;
    text-align: center;
    line-height: 1rem;
    margin-bottom: 0px;

    > *:not(.full-match) {
      display: none;
    }
  }
  :deep(.car-image) {
    width: 100%;
    height: calc(var(--column-width) / 3 * 2);
    display: block;
    border: 1px solid $gray-light;
  }
  :deep(.title-wrapper) {
    order: 1;
  }
  :deep(.spec-group) {
    margin-bottom: 0;
  }

  .standard-options-container {
    margin-top: 1rem;
  }

  .drive {
    white-space: normal;
    overflow: auto;
    height: 2.8em;
  }

  h4 {
    font-size: 0.75rem;
    line-height: 1rem;
    font-weight: $font-weight-bold;
    color: $gray-darker;
    margin: 0.25rem 0;
  }

  .cars-container {
    display: grid;
    row-gap: 1rem;
    grid-template-columns:
      10rem
      calc(var(--column-width) + 1rem) auto;
    grid-template-rows: calc(var(--column-width) / 3 * 2 + 1.5rem) min-content auto;
    grid-template-areas:
      'info-label chosen-car matching-cars'
      'match-filters chosen-car matching-cars'
      'container-actions container-actions container-actions';
    margin-bottom: 1rem;
  }

  :deep(.car-container) button {
    width: 100%;
  }

  .info-label {
    grid-area: info-label;

    h5 {
      font-size: $font-size-base;
      line-height: normal;
    }
  }

  .match-filters {
    grid-area: match-filters;
    padding-right: 1rem;
    white-space: nowrap;

    dl {
      padding: 0;
    }

    dt {
      margin: 1.75rem 0 1em;
    }

    .options-heading {
      order: 2;
      margin: 0;
    }

    :deep(label) {
      width: 100%;
      display: block;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  .chosen-car {
    grid-area: chosen-car;
    padding-left: 0.5rem;
    padding-right: 0.5rem;
    background: $gray-lightest;

    h4 {
      text-align: center;
    }
  }
  .matching-cars-viewport {
    grid-area: matching-cars;
    display: flex;
    padding-left: 0.5rem;
    overflow-x: auto;
  }

  .matching-cars {
    list-style: none;
    display: flex;
    gap: 0.5rem;
  }

  .car-column {
    width: calc(var(--column-width) + 1rem);
  }

  .container-actions {
    grid-area: container-actions;
    display: flex;
    justify-content: flex-end;
    white-space: nowrap;

    button {
      vertical-align: middle;
    }
  }

  .no-stock-message {
    align-self: center;
    font-weight: bold;
  }
}
</style>
